if (!customElements.get('product-sticky-bar')) {
  const CHAT_WIDGET_SELECTORS = [
    '#chat-bubble',
    '#wa-chat-widget',
    '#whatsapp-chat-widget',
    '#whatsapp-widget',
    '#whatsapp-widget-root',
    '.whatsapp-chat-widget',
    '.wa-chat-widget',
    '.wa-chat-button',
    '[class*="whatsapp-chat"]',
    '[id*="whatsapp"]',
    '[class*="whatsapp-widget"]',
  ];

  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.action = this.dataset.action || 'add_to_cart';
        this.threshold = parseFloat(this.dataset.scrollThreshold || '75') / 100;
        this.customLabel = this.dataset.customLabel || '';
        this.infoEl = document.getElementById(`ProductInfo-${this.sectionId}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.button = this.querySelector('[data-sticky-bar-button]');
        this.labelEl = this.querySelector('[data-sticky-bar-label]');

        if (!this.infoEl || !this.button) return;

        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }

        this.onScrollHandler = this.onScroll.bind(this);
        this.button.addEventListener('click', this.onButtonClick.bind(this));
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });
        window.addEventListener('resize', this.onScrollHandler, { passive: true });

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId === this.sectionId) {
              requestAnimationFrame(() => this.syncWithMainButton());
            }
          });
        }

        this.observeMainButton();
        this.initChatWidgetObserver();
        this.syncWithMainButton();
        this.onScroll();

        if (window.Shopify?.designMode) {
          this.classList.add('is-visible');
          this.removeAttribute('hidden');
          this.updatePageOffsets(true);
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScrollHandler);
        window.removeEventListener('resize', this.onScrollHandler);
        this.variantChangeUnsubscriber?.();
        this.mainButtonObserver?.disconnect();
        this.chatWidgetObserver?.disconnect();
        this.updatePageOffsets(false);
      }

      initChatWidgetObserver() {
        this.chatWidgetObserver = new MutationObserver(() => {
          if (!this.classList.contains('is-visible')) return;

          if (this.chatWidgetAdjustFrame) cancelAnimationFrame(this.chatWidgetAdjustFrame);
          this.chatWidgetAdjustFrame = requestAnimationFrame(() => {
            this.adjustChatWidgets(true);
          });
        });

        this.chatWidgetObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      getChatWidgetElements() {
        const elements = new Set();

        CHAT_WIDGET_SELECTORS.forEach((selector) => {
          try {
            document.querySelectorAll(selector).forEach((element) => {
              if (this.isFloatingChatWidget(element)) elements.add(element);
            });
          } catch (error) {
            /* ignore invalid selectors */
          }
        });

        document.querySelectorAll('body > div, body > iframe').forEach((element) => {
          if (!this.isFloatingChatWidget(element)) return;

          const descriptor = `${element.id} ${element.className}`.toLowerCase();
          if (/whatsapp|wa-chat|chat-bubble/.test(descriptor)) {
            elements.add(element);
          }
        });

        return [...elements];
      }

      isFloatingChatWidget(element) {
        if (!(element instanceof HTMLElement)) return false;

        const style = window.getComputedStyle(element);
        if (style.position !== 'fixed' && style.position !== 'absolute') return false;

        const rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        return rect.bottom > window.innerHeight - 220 && rect.right > window.innerWidth - 180;
      }

      adjustChatWidgets(show) {
        const offset = show ? `${this.offsetHeight + 12}px` : null;

        this.getChatWidgetElements().forEach((element) => {
          if (show && offset) {
            if (!element.dataset.stickyBarAdjusted) {
              element.dataset.stickyBarAdjusted = 'true';
              element.dataset.stickyBarOriginalBottom = element.style.bottom || '';
            }

            element.style.setProperty(
              'bottom',
              `calc(${offset} + env(safe-area-inset-bottom, 0px))`,
              'important'
            );
            element.style.setProperty('transition', 'bottom 0.4s cubic-bezier(0.22, 1, 0.36, 1)', 'important');
          } else if (element.dataset.stickyBarAdjusted) {
            if (element.dataset.stickyBarOriginalBottom) {
              element.style.setProperty('bottom', element.dataset.stickyBarOriginalBottom);
            } else {
              element.style.removeProperty('bottom');
            }

            element.style.removeProperty('transition');
            delete element.dataset.stickyBarAdjusted;
            delete element.dataset.stickyBarOriginalBottom;
          }
        });
      }

      updatePageOffsets(show) {
        const isMobile = window.innerWidth < 750;
        const shouldOffset = show && isMobile;

        document.body.classList.toggle('product-sticky-bar-visible', shouldOffset);

        if (shouldOffset) {
          document.documentElement.style.setProperty('--product-sticky-bar-offset', `${this.offsetHeight + 12}px`);
          this.adjustChatWidgets(true);
          return;
        }

        document.documentElement.style.removeProperty('--product-sticky-bar-offset');
        this.adjustChatWidgets(false);
      }

      onButtonClick() {
        if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;

        if (this.action === 'scroll_to_product') {
          this.scrollToProductInfo();
          return;
        }

        if (this.mainButton && !this.mainButton.disabled) {
          this.mainButton.click();
        }
      }

      scrollToProductInfo() {
        const header =
          document.querySelector('sticky-header') ||
          document.querySelector('.shopify-section-header-sticky') ||
          document.querySelector('.section-header');
        const headerOffset = header ? header.offsetHeight : 0;
        const top = this.infoEl.getBoundingClientRect().top + window.scrollY - headerOffset - 16;

        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }

      onScroll() {
        if (window.innerWidth >= 750) {
          this.setVisible(false);
          return;
        }

        const rect = this.infoEl.getBoundingClientRect();
        const infoTop = window.scrollY + rect.top;
        const infoHeight = this.infoEl.offsetHeight;
        const scrolledIntoInfo = window.scrollY + window.innerHeight - infoTop;
        const show = scrolledIntoInfo >= infoHeight * this.threshold;

        this.setVisible(show);
      }

      setVisible(show) {
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('hidden', !show);
        this.updatePageOffsets(show);
      }

      syncWithMainButton() {
        if (!this.labelEl) return;

        if (this.customLabel) {
          this.labelEl.textContent = this.customLabel;
        } else if (this.action === 'scroll_to_product') {
          this.labelEl.textContent = this.labelEl.dataset.scrollLabel || 'View product';
        } else if (this.mainButton) {
          const mainLabel = this.mainButton.querySelector('span')?.textContent?.trim();
          if (mainLabel) this.labelEl.textContent = mainLabel;
        }

        if (this.action === 'scroll_to_product') {
          this.button.disabled = false;
          this.button.removeAttribute('aria-disabled');
        } else if (this.mainButton) {
          this.button.disabled = this.mainButton.disabled;
          const ariaDisabled = this.mainButton.getAttribute('aria-disabled');
          if (ariaDisabled === 'true') {
            this.button.setAttribute('aria-disabled', 'true');
          } else {
            this.button.removeAttribute('aria-disabled');
          }
        }

        if (this.mainButton) {
          const isLoading = this.mainButton.classList.contains('loading');
          this.button.classList.toggle('loading', isLoading);
          this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);

          if (isLoading) {
            this.button.setAttribute('aria-disabled', 'true');
          } else if (this.action !== 'scroll_to_product' && !this.mainButton.disabled) {
            this.button.removeAttribute('aria-disabled');
          }
        }
      }

      observeMainButton() {
        if (!this.mainButton) return;

        this.mainButtonObserver = new MutationObserver(() => this.syncWithMainButton());
        this.mainButtonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          subtree: true,
          childList: true,
        });
      }
    }
  );
}
